import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { page } from "@/lib/analytics";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import ProductsPage from "@/pages/ProductsPage";
import SolutionsPage from "@/pages/SolutionsPage";
import AboutPage from "@/pages/AboutPage";
import ResourcesPage from "@/pages/ResourcesPage";
import ContactPage from "@/pages/ContactPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Products from "@/pages/dashboard/Products";
import QRCodePage from "@/pages/dashboard/QRCodePage";
import SalespersonsPage from "@/pages/dashboard/SalespersonsPage";
import RetailerVisualizer from "@/pages/dashboard/RetailerVisualizer";
import History from "@/pages/dashboard/History";
import Billing from "@/pages/dashboard/Billing";
import DashboardContactUs from "@/pages/dashboard/ContactUs";
import SalespersonUsage from "@/pages/dashboard/SalespersonUsage";

import VerifyEmailSent from "@/pages/VerifyEmailSent";
import VerifyEmail from "@/pages/VerifyEmail";
import BookDemo from "@/pages/BookDemo";
import ContactUs from "@/pages/ContactUs";
import CustomerVisualizer from "@/pages/customer/CustomerVisualizer";
import SalesVisualizer from "@/pages/sales/SalesVisualizer";
import SalesLogin from "@/pages/sales/SalesLogin";
import SalesDashboard from "@/pages/sales/SalesDashboard";
import SalesQueue from "@/pages/sales/SalesQueue";
import AdminPanel from "@/pages/admin/AdminPanel";
import AdminLogin from "@/pages/admin/AdminLogin";
import NotFound from "@/pages/NotFound";

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    page(location.pathname + location.search);
    if (location.hash) {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start" });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PageTracker />
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/blogs" element={<ResourcesPage />} />
            <Route path="/old-landing" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/verify-email-sent" element={<VerifyEmailSent />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/book-demo" element={<BookDemo />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/old-contact" element={<ContactUs />} />
            <Route path="/r/:slug" element={<CustomerVisualizer />} />

            {/* TEMP-DEV-ROUTE: remove */}
            <Route path="/__dev" element={<DashboardLayout />}>
              <Route path="products" element={<Products />} />
            </Route>

            <Route element={<ProtectedRoute role="retailer" />}>
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Overview />} />
                <Route path="products" element={<Products />} />
                <Route path="qr" element={<QRCodePage />} />
                <Route path="sales-team" element={<SalespersonsPage />} />
              <Route path="sales-team/usage" element={<SalespersonUsage />} />
                <Route path="history" element={<History />} />
                <Route path="billing" element={<Billing />} />
                <Route path="contact" element={<DashboardContactUs />} />
              </Route>
              <Route path="/visualize" element={<RetailerVisualizer />} />
            </Route>

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route element={<ProtectedRoute role="admin" />}>
              <Route path="/admin" element={<AdminPanel />} />
            </Route>

            <Route path="/sales/login" element={<SalesLogin />} />
            <Route element={<ProtectedRoute role="salesperson" />}>
              <Route path="/sales" element={<SalesDashboard />} />
              <Route path="/sales/visualizer" element={<SalesVisualizer />} />
              <Route path="/sales/queue" element={<SalesQueue />} />
            </Route>

            {/* 404 - Must be last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
